/**
 * useRoutePrefetch Hook
 * Prefetches route components on hover or focus for better performance
 */

import { useCallback, useRef } from 'react';

// Type for route modules that can be prefetched
type RouteModule = () => Promise<{ default: React.ComponentType<any> }>;

// Map of routes to their lazy import functions
const routeModules: Record<string, RouteModule> = {
  '/upload': () =>
    import('../pages/UploadPage').then((module) => ({
      default: module.UploadPage,
    })),
  '/manual': () => import('../pages/ManualEntryPage'),
  '/invoice-results': () => import('../pages/InvoiceResultsPage'),
};

// Cache for prefetched modules to avoid duplicate fetches
const prefetchCache = new Map<
  string,
  Promise<{ default: React.ComponentType<any> }>
>();

export const useRoutePrefetch = () => {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const prefetchRoute = useCallback((route: string) => {
    // Check if we have a module for this route
    const moduleLoader = routeModules[route];
    if (!moduleLoader) {
      return;
    }

    // Check if already cached
    if (prefetchCache.has(route)) {
      return;
    }

    // Prefetch the module
    const prefetchPromise = moduleLoader();
    prefetchCache.set(route, prefetchPromise);

    // Clean up cache after some time to prevent memory leaks
    setTimeout(() => {
      prefetchCache.delete(route);
    }, 30000); // 30 seconds
  }, []);

  const prefetchOnHover = useCallback(
    (route: string) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Prefetch after a short delay to avoid unnecessary prefetches
      timeoutRef.current = setTimeout(() => {
        prefetchRoute(route);
      }, 150); // 150ms delay
    },
    [prefetchRoute],
  );

  const cancelPrefetch = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const prefetchOnFocus = useCallback(
    (route: string) => {
      // Prefetch immediately on focus for keyboard navigation
      prefetchRoute(route);
    },
    [prefetchRoute],
  );

  return {
    prefetchRoute,
    prefetchOnHover,
    prefetchOnFocus,
    cancelPrefetch,
  };
};

export default useRoutePrefetch;
